import api from "./api";

const reportService = {
  // Get Inventory Report
  getInventoryReport: async (filters = {}) => {
    try {
      // Use products API directly since it supports category_id and sub_category_id filters
      const productParams = new URLSearchParams();
      if (filters.category_id !== undefined && filters.category_id !== null && filters.category_id !== "") {
        productParams.append("category_id", String(filters.category_id).trim());
      }
      if (filters.sub_category_id !== undefined && filters.sub_category_id !== null && filters.sub_category_id !== "") {
        productParams.append("sub_category_id", String(filters.sub_category_id).trim());
      }
      // Note: product_id filtering is done client-side as API may not support it
      if (filters.page) productParams.append("page", filters.page);
      if (filters.limit) productParams.append("limit", filters.limit || 1000);
      else productParams.append("limit", "1000"); // Get all products by default

      const apiUrl = `/api/products?${productParams.toString()}`;
      console.log("=== API CALL ===");
      console.log("URL:", apiUrl);
      console.log("Filters received:", filters);
      console.log("Query params:", productParams.toString());
      
      const response = await api.get(apiUrl);
      
      console.log("=== API RESPONSE ===");
      console.log("Full response:", response);
      console.log("Response data:", response.data);
      console.log("Response data type:", typeof response.data);
      console.log("Is array?", Array.isArray(response.data));
      
      // Handle different response structures
      let products = [];
      if (response && response.data) {
        // Try response.data.data.products first (most common structure)
        if (response.data.data && response.data.data.products && Array.isArray(response.data.data.products)) {
          products = response.data.data.products;
          console.log("Found products in response.data.data.products");
        } 
        // Try response.data.products
        else if (response.data.products && Array.isArray(response.data.products)) {
          products = response.data.products;
          console.log("Found products in response.data.products");
        } 
        // Try response.data as array
        else if (Array.isArray(response.data)) {
          products = response.data;
          console.log("Found products in response.data (array)");
        } 
        // Try response.data.data as array
        else if (response.data.data && Array.isArray(response.data.data)) {
          products = response.data.data;
          console.log("Found products in response.data.data (array)");
        }
        else {
          console.warn("Could not find products array in response. Response structure:", {
            hasData: !!response.data,
            hasDataData: !!response.data?.data,
            hasDataProducts: !!response.data?.products,
            hasDataDataProducts: !!response.data?.data?.products,
            dataType: typeof response.data,
            isDataArray: Array.isArray(response.data),
            isDataDataArray: Array.isArray(response.data?.data)
          });
        }
      } else {
        console.warn("Response or response.data is missing");
      }
      
      // Ensure products is an array
      if (!Array.isArray(products)) {
        console.warn("Products is not an array, converting to empty array. Type:", typeof products, "Value:", products);
        products = [];
      }
      
      console.log("=== EXTRACTED PRODUCTS ===");
      console.log("Count:", products.length);
      if (products.length > 0) {
        console.log("First product sample:", products[0]);
      }

      // Apply client-side filters (product_id, date range)
      let filteredProducts = products;

      // Filter by product_id if provided
      if (filters.product_id) {
        filteredProducts = filteredProducts.filter((product) => {
          const productId = product.id?.toString() || String(product.id || "");
          const filterProductId = filters.product_id.toString();
          return productId === filterProductId;
        });
      }

      // Filter by date range if provided
      if (filters.start_date || filters.end_date) {
        filteredProducts = filteredProducts.filter((product) => {
          const productDate = new Date(
            product.created_at || product.updated_at
          );
          if (
            filters.start_date &&
            productDate < new Date(filters.start_date)
          ) {
            return false;
          }
          if (filters.end_date) {
            const endDate = new Date(filters.end_date);
            endDate.setHours(23, 59, 59, 999); // Include the entire end date
            if (productDate > endDate) {
              return false;
            }
          }
          return true;
        });
      }

      return {
        products: filteredProducts,
        data: filteredProducts,
        total: filteredProducts.length,
      };
    } catch (error) {
      console.error("Error fetching inventory report:", error);
      throw error;
    }
  },

  // Get Purchase Report
  getPurchaseReport: async (filters = {}) => {
    try {
      const params = new URLSearchParams();

      if (filters.category_id)
        params.append("category_id", filters.category_id);
      if (filters.sub_category_id)
        params.append("sub_category_id", filters.sub_category_id);
      if (filters.product_id) params.append("product_id", filters.product_id);
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);
      if (filters.page) params.append("page", filters.page);
      if (filters.limit) params.append("limit", filters.limit);

      const response = await api.get(
        `/api/purchase-orders/report?${params.toString()}`
      );
      return response.data.data || response.data;
    } catch (error) {
      console.error("Error fetching purchase report:", error);
      // Fallback: get purchase orders with filters
      try {
        const params = new URLSearchParams();
        if (filters.start_date) params.append("start_date", filters.start_date);
        if (filters.end_date) params.append("end_date", filters.end_date);

        const response = await api.get(
          `/api/purchase-orders?${params.toString()}`
        );
        const purchaseOrders =
          response.data.data?.purchaseOrders ||
          response.data.purchaseOrders ||
          response.data.data ||
          [];

        // Filter by category/subcategory/product if provided
        let filteredOrders = purchaseOrders;
        if (
          filters.category_id ||
          filters.sub_category_id ||
          filters.product_id
        ) {
          // We'll need to fetch items for each order to filter
          // For now, return all orders
          filteredOrders = purchaseOrders;
        }

        return {
          purchaseOrders: filteredOrders,
          total: filteredOrders.length,
        };
      } catch (fallbackError) {
        console.error("Fallback purchase report error:", fallbackError);
        throw error;
      }
    }
  },

  // Get Orders Report
  getOrdersReport: async (filters = {}) => {
    try {
      const params = new URLSearchParams();

      if (filters.category_id)
        params.append("category_id", filters.category_id);
      if (filters.sub_category_id)
        params.append("sub_category_id", filters.sub_category_id);
      if (filters.product_id) params.append("product_id", filters.product_id);
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);
      if (filters.status) params.append("status", filters.status);
      if (filters.page) params.append("page", filters.page);
      if (filters.limit) params.append("limit", filters.limit);

      const response = await api.get(
        `/api/admin/orders/report?${params.toString()}`
      );
      return response.data.data || response.data;
    } catch (error) {
      console.error("Error fetching orders report:", error);
      // Fallback: get orders with filters
      try {
        const params = new URLSearchParams();
        if (filters.start_date) params.append("start_date", filters.start_date);
        if (filters.end_date) params.append("end_date", filters.end_date);
        if (filters.status) params.append("status", filters.status);

        const response = await api.get(
          `/api/admin/orders?${params.toString()}`
        );
        const orders =
          response.data.data?.orders ||
          response.data.orders ||
          response.data.data ||
          [];

        return {
          orders: orders,
          total: orders.length,
        };
      } catch (fallbackError) {
        console.error("Fallback orders report error:", fallbackError);
        throw error;
      }
    }
  },

  // Get Revenue Report
  getRevenueReport: async (filters = {}) => {
    try {
      const params = new URLSearchParams();

      if (filters.category_id)
        params.append("category_id", filters.category_id);
      if (filters.sub_category_id)
        params.append("sub_category_id", filters.sub_category_id);
      if (filters.product_id) params.append("product_id", filters.product_id);
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);

      const response = await api.get(
        `/api/admin/revenue/report?${params.toString()}`
      );
      return response.data.data || response.data;
    } catch (error) {
      console.error("Error fetching revenue report:", error);
      throw error;
    }
  },

  // Get Cancelled Orders Report
  getCancelledOrdersReport: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.category_id) params.append("category_id", filters.category_id);
      if (filters.sub_category_id) params.append("sub_category_id", filters.sub_category_id);
      if (filters.product_id) params.append("product_id", filters.product_id);
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);
      if (filters.user_id) params.append("user_id", filters.user_id);
      if (filters.page) params.append("page", filters.page);
      if (filters.limit) params.append("limit", filters.limit);

      const response = await api.get(
        `/api/admin/orders/report/cancelled?${params.toString()}`
      );
      return response.data.data || response.data;
    } catch (error) {
      console.error("Error fetching cancelled orders report:", error);
      // Fallback to admin orders list filtered by status=cancelled
      try {
        const params = new URLSearchParams();
        params.append("status", "cancelled");
        if (filters.start_date) params.append("start_date", filters.start_date);
        if (filters.end_date) params.append("end_date", filters.end_date);
        if (filters.user_id) params.append("user_id", filters.user_id);

        const response = await api.get(`/api/admin/orders?${params.toString()}`);
        const orders =
          response.data.data?.orders ||
          response.data.orders ||
          response.data.data ||
          [];
        return { orders, total: orders.length };
      } catch (fallbackError) {
        console.error("Fallback cancelled orders report error:", fallbackError);
        throw error;
      }
    }
  },

  // Get Low Stock Products Report
  getLowStockProductsReport: async (filters = {}) => {
    try {
      const params = new URLSearchParams();

      if (filters.category_id)
        params.append("category_id", filters.category_id);
      if (filters.sub_category_id)
        params.append("sub_category_id", filters.sub_category_id);
      if (filters.product_id) params.append("product_id", filters.product_id);
      if (filters.threshold) params.append("threshold", filters.threshold);
      if (filters.page) params.append("page", filters.page);
      if (filters.limit) params.append("limit", filters.limit);

      const response = await api.get(
        `/api/inventory/low-stock?${params.toString()}`
      );
      return response.data.data || response.data;
    } catch (error) {
      console.error("Error fetching low stock products report:", error);
      // Fallback: get low stock products
      try {
        const params = new URLSearchParams();
        if (filters.threshold) params.append("threshold", filters.threshold);

        const response = await api.get(
          `/api/stock-adjustments/low-stock?${params.toString()}`
        );
        const products =
          response.data.data?.products ||
          response.data.products ||
          response.data.data ||
          [];

        // Filter by category/subcategory/product if provided
        let filteredProducts = products;
        if (filters.category_id) {
          filteredProducts = filteredProducts.filter(
            (p) => p.category_id === filters.category_id
          );
        }
        if (filters.sub_category_id) {
          filteredProducts = filteredProducts.filter(
            (p) => p.sub_category_id === filters.sub_category_id
          );
        }
        if (filters.product_id) {
          filteredProducts = filteredProducts.filter(
            (p) => p.id === filters.product_id
          );
        }

        return {
          products: filteredProducts,
          total: filteredProducts.length,
        };
      } catch (fallbackError) {
        console.error("Fallback low stock report error:", fallbackError);
        throw error;
      }
    }
  },

  // Download report as CSV/PDF
  downloadReport: async (reportType, filters = {}, format = "csv") => {
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach((key) => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });
      params.append("format", format);

      const response = await api.get(
        `/api/reports/${reportType}/download?${params.toString()}`,
        {
          responseType: "blob",
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Error downloading ${reportType} report:`, error);
      throw error;
    }
  },

  // Download report as PDF (server-side)
  downloadReportPDF: async (reportType, filters = {}) => {
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach((key) => {
        if (
          filters[key] !== undefined &&
          filters[key] !== null &&
          filters[key] !== ""
        ) {
          params.append(key, filters[key]);
        }
      });

      let endpoint = "";
      switch (reportType) {
        case "inventory":
          endpoint = `/api/inventory/reports/download-pdf`;
          break;
        case "orders":
          endpoint = `/api/admin/orders/report/download-pdf`;
          break;
        case "cancelled-orders":
          endpoint = `/api/admin/orders/report/cancelled/download-pdf`;
          break;
        case "purchase":
          endpoint = `/api/purchase-orders/report/download-pdf`;
          break;
        case "low-stock":
          endpoint = `/api/inventory/low-stock/download-pdf`;
          break;
        case "revenue":
          endpoint = `/api/admin/revenue/report/download-pdf`;
          break;
        default:
          throw new Error(`Unknown report type: ${reportType}`);
      }

      const url = `${endpoint}?${params.toString()}`;
      console.log(`[ReportService] Downloading PDF from: ${url}`);
      
      const response = await api.get(url, {
        responseType: "blob",
      });

      // Check response status first
      if (response.status < 200 || response.status >= 300) {
        // If error, try to extract error message from blob
        let errorMessage = `HTTP ${response.status}: Failed to download PDF`;
        try {
          const contentType = response.headers['content-type'] || response.headers['Content-Type'] || '';
          if (contentType.includes('application/json')) {
            const text = await response.data.text();
            const errorData = JSON.parse(text);
            errorMessage = errorData.message || errorData.error || errorMessage;
          }
        } catch (parseError) {
          console.warn('[ReportService] Could not parse error response:', parseError);
        }
        const error = new Error(errorMessage);
        error.response = response;
        throw error;
      }

      // Check content type - if it's JSON, it's likely an error response
      const contentType = response.headers['content-type'] || response.headers['Content-Type'] || '';
      if (contentType.includes('application/json')) {
        // This shouldn't happen for a successful PDF download, but handle it
        try {
          const text = await response.data.text();
          const errorData = JSON.parse(text);
          const error = new Error(errorData.message || errorData.error || 'Failed to download PDF');
          error.response = { data: errorData, status: response.status };
          throw error;
        } catch (parseError) {
          // If parsing fails, it might actually be a valid response
          console.warn('[ReportService] Unexpected JSON content type but could not parse, treating as valid PDF');
          return response.data;
        }
      }

      // Valid PDF response
      return response.data;
    } catch (error) {
      console.error(`Error downloading ${reportType} PDF:`, error);
      
      // If error response is a blob (JSON error), try to extract the message
      if (error.response && error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          error.message = errorData.message || errorData.error || error.message;
        } catch (parseError) {
          // If parsing fails, use original error message
        }
      }
      
      throw error;
    }
  },

  // Download delivery profile history report as PDF
  downloadDeliveryProfileHistoryPDF: async (deliveryProfileId, filters = {}) => {
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach((key) => {
        if (
          filters[key] !== undefined &&
          filters[key] !== null &&
          filters[key] !== ""
        ) {
          params.append(key, filters[key]);
        }
      });

      const response = await api.get(
        `/api/delivery-profiles/${deliveryProfileId}/delivery-history/download-pdf?${params.toString()}`,
        {
          responseType: "blob",
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error downloading delivery profile history PDF:", error);
      throw error;
    }
  },
};

export default reportService;
